import { createFileRoute, Link, notFound } from '@tanstack/react-router';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSlug from 'rehype-slug';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { BlogMeta } from '@/components/blog/BlogMeta';
import { BlogTags } from '@/components/blog/BlogTags';
import { ShareButtons } from '@/components/blog/ShareButtons';
import { RelatedPosts } from '@/components/blog/RelatedPosts';
import { TableOfContents } from '@/components/blog/TableOfContents';
import WhatsAppButton from '@/components/react/WhatsAppButton';
import { calculateReadingTime, extractHeadings } from '@/lib/blog-utils';
import { categoryLabels } from '@/types/blog';
import type { BlogPost } from '@/types/blog';

interface BlogPostData {
  post: BlogPost;
  relatedPosts: BlogPost[];
  readingTime: number;
  headings: { id: string; text: string; level: number }[];
  canonicalUrl: string;
}

async function loadPostData(slug: string): Promise<BlogPostData> {
  const { getBlogPost, getRelatedPosts } = await import('@/lib/blog.server');
  const post = await getBlogPost(slug);

  if (!post) {
    throw notFound();
  }

  const relatedPosts = await getRelatedPosts(slug, post.frontmatter.category);
  const readingTime = calculateReadingTime(post.content);
  const headings = extractHeadings(post.content);
  const canonicalUrl = `https://patriar.com.br/blog/${slug}`;

  return {
    post,
    relatedPosts,
    readingTime,
    headings,
    canonicalUrl,
  };
}

export const Route = createFileRoute('/blog/$slug')({
  loader: ({ params }) => loadPostData(params.slug),
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { post, canonicalUrl } = loaderData;
    return {
      meta: [
        { title: `${post.frontmatter.title} | Patriar Blog` },
        { name: 'description', content: post.frontmatter.description },
        { property: 'og:title', content: post.frontmatter.title },
        { property: 'og:description', content: post.frontmatter.description },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: canonicalUrl },
        { property: 'og:image', content: post.frontmatter.image.src },
        { property: 'article:published_time', content: post.frontmatter.publishDate },
        ...(post.frontmatter.updatedDate
          ? [{ property: 'article:modified_time', content: post.frontmatter.updatedDate }]
          : []),
        { property: 'article:author', content: post.frontmatter.author.name },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: post.frontmatter.title },
        { name: 'twitter:description', content: post.frontmatter.description },
        { name: 'twitter:image', content: post.frontmatter.image.src },
      ],
      links: [{ rel: 'canonical', href: canonicalUrl }],
    };
  },
  component: BlogPostPage,
  notFoundComponent: () => {
    return (
      <>
        <Header />
        <main className="py-24">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold text-navy-900 mb-4">Artigo não encontrado</h1>
            <p className="text-neutral-600 mb-8">
              O artigo que você está procurando não existe ou foi removido.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              Voltar ao Blog
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  },
});

function BlogPostPage() {
  const { post, relatedPosts, readingTime, headings, canonicalUrl } = Route.useLoaderData();

  // JSON-LD structured data
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.frontmatter.title,
    description: post.frontmatter.description,
    image: post.frontmatter.image.src,
    datePublished: post.frontmatter.publishDate,
    dateModified: post.frontmatter.updatedDate || post.frontmatter.publishDate,
    author: {
      '@type': 'Person',
      name: post.frontmatter.author.name,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Patriar',
      logo: {
        '@type': 'ImageObject',
        url: 'https://patriar.com.br/favicon.svg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
  };

  return (
    <>
      <Header />
      <main>
        {/* JSON-LD Script */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />

        <article className="py-12 lg:py-20">
          <div className="container mx-auto px-4 lg:px-8">
            {/* Back to Blog */}
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-gold-600 hover:text-gold-700 transition-colors mb-8"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Voltar ao Blog
            </Link>

            {/* Article Header */}
            <header className="max-w-3xl mx-auto text-center mb-12">
              <span className="text-gold-600 font-semibold text-sm uppercase tracking-wider">
                {categoryLabels[post.frontmatter.category]}
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy-900 mt-3 mb-6 leading-tight">
                {post.frontmatter.title}
              </h1>
              <p className="text-lg text-neutral-600 mb-8">{post.frontmatter.description}</p>
              <BlogMeta
                author={post.frontmatter.author}
                publishDate={post.frontmatter.publishDate}
                readingTime={readingTime}
                updatedDate={post.frontmatter.updatedDate}
              />
            </header>

            {/* Featured Image */}
            <div className="max-w-4xl mx-auto mb-12">
              <img
                src={post.frontmatter.image.src}
                alt={post.frontmatter.image.alt}
                className="w-full rounded-2xl shadow-lg"
              />
            </div>

            {/* Content Grid with TOC */}
            <div className="max-w-6xl mx-auto lg:grid lg:grid-cols-[1fr_280px] lg:gap-12">
              {/* Main Content */}
              <div className="prose prose-lg max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw, rehypeSlug]}
                >
                  {post.content}
                </ReactMarkdown>
              </div>

              {/* Table of Contents (Desktop) */}
              <aside className="hidden lg:block">
                <TableOfContents headings={headings} />
              </aside>
            </div>

            {/* Tags */}
            {post.frontmatter.tags.length > 0 && (
              <div className="max-w-3xl mx-auto mt-12">
                <BlogTags tags={post.frontmatter.tags} />
              </div>
            )}

            {/* Share Buttons */}
            <div className="max-w-3xl mx-auto mt-12 pt-8 border-t border-neutral-200">
              <ShareButtons title={post.frontmatter.title} url={canonicalUrl} />
            </div>
          </div>
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="py-12 border-t border-neutral-200">
            <div className="container mx-auto px-4 lg:px-8">
              <RelatedPosts posts={relatedPosts} />
            </div>
          </section>
        )}
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
}
